import { useState, useEffect } from 'react';
import { X, ArrowRight, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LIGHTBOX_DISMISSED_KEY = 'revq_workflow_lightbox_dismissed';
const MODE_KEY = 'revq_onboarding_mode';
const INTEGRATION_KEY = 'revq_integration_type';
const REVQ_LOGO_BLACK = 'https://customer-assets.emergentagent.com/job_medcycle-metrics/artifacts/zakm44og_revQ_logo_black.png';

/* ── Sparkle SVG inline (gray for neutral theme) ── */
const SparkleIcon = ({ size = 11, color = '#888' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><path d="M8 0L9.8 6.2L16 8L9.8 9.8L8 16L6.2 9.8L0 8L6.2 6.2L8 0Z" fill={color} /></svg>
);

/* ── Step card components ── */
const HumanStep = ({ num, title, badge, badgeStyle, desc, lensCta, onLensClick, label = 'You do this' }) => (
  <div style={{ background: '#fff', border: '0.5px solid #ebebeb', borderRadius: 9, padding: '10px 13px', display: 'flex', alignItems: 'flex-start', gap: 10 }} data-testid={`step-human-${num}`}>
    <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#111', color: '#fff', fontSize: 10, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{num}</div>
    <div style={{ flex: 1 }}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span style={{ fontSize: 11, fontWeight: 500, color: '#111' }}>{title}</span>
        {badge && <span style={{ fontSize: 8, fontWeight: 500, padding: '1px 5px', borderRadius: 6, ...(badgeStyle || { background: '#f0f0f0', color: '#555' }) }}>{badge}</span>}
      </div>
      <p style={{ fontSize: 10, color: '#555', lineHeight: '1.5', marginTop: 3 }}>{desc}</p>
      {lensCta && <button onClick={onLensClick} style={{ fontSize: 9, color: '#555', background: 'none', border: '0.5px solid #e0e0e0', borderRadius: 5, padding: '2px 8px', marginTop: 5, cursor: 'pointer' }} data-testid={`lens-cta-${num}`}>{lensCta}</button>}
    </div>
    <span style={{ fontSize: 9, color: '#bbb', flexShrink: 0, whiteSpace: 'nowrap', marginTop: 2 }}>{label}</span>
  </div>
);

const AIStep = ({ title, badge, badgeStyle, desc, label = 'AI handles this' }) => (
  <div style={{ background: '#fafafa', border: '0.5px solid #f0f0f0', borderRadius: 9, padding: '10px 13px', display: 'flex', alignItems: 'flex-start', gap: 10 }} data-testid="step-ai">
    <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}><SparkleIcon size={11} color="#888" /></div>
    <div style={{ flex: 1 }}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span style={{ fontSize: 11, fontWeight: 500, color: '#111' }}>{title}</span>
        {badge && <span style={{ fontSize: 8, fontWeight: 500, padding: '1px 5px', borderRadius: 6, ...(badgeStyle || { background: '#f0f0f0', color: '#555' }) }}>{badge}</span>}
      </div>
      <p style={{ fontSize: 10, color: '#555', lineHeight: '1.5', marginTop: 3 }}>{desc}</p>
    </div>
    <span style={{ fontSize: 9, color: '#bbb', flexShrink: 0, whiteSpace: 'nowrap', marginTop: 2 }}>{label}</span>
  </div>
);

const AutopilotStep = ({ title, badge, desc, lensCta, onLensClick, label = 'AI submits this' }) => (
  <div style={{ background: '#f5f5f5', border: '0.5px solid #e0e0e0', borderRadius: 9, padding: '10px 13px', display: 'flex', alignItems: 'flex-start', gap: 10 }} data-testid="step-autopilot">
    <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}><SparkleIcon size={11} color="#fff" /></div>
    <div style={{ flex: 1 }}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span style={{ fontSize: 11, fontWeight: 500, color: '#111' }}>{title}</span>
        {badge && <span style={{ fontSize: 8, fontWeight: 500, padding: '1px 5px', borderRadius: 6, background: '#111', color: '#fff' }}>{badge}</span>}
      </div>
      <p style={{ fontSize: 10, color: '#555', lineHeight: '1.5', marginTop: 3 }}>{desc}</p>
      {lensCta && <button onClick={onLensClick} style={{ fontSize: 9, color: '#555', background: 'none', border: '0.5px solid #e0e0e0', borderRadius: 5, padding: '2px 8px', marginTop: 5, cursor: 'pointer' }}>{lensCta}</button>}
    </div>
    <span style={{ fontSize: 9, color: '#bbb', flexShrink: 0, whiteSpace: 'nowrap', marginTop: 2 }}>{label}</span>
  </div>
);

const Divider = ({ label }) => (
  <div className="flex items-center gap-3" style={{ margin: '4px 0' }}>
    <div style={{ flex: 1, height: '0.5px', background: '#f0f0f0' }} />
    <span style={{ fontSize: 9, color: '#ccc', whiteSpace: 'nowrap' }}>{label}</span>
    <div style={{ flex: 1, height: '0.5px', background: '#f0f0f0' }} />
  </div>
);

const AutopilotHighlight = () => (
  <div style={{ background: '#f5f5f5', border: '0.5px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 10 }} data-testid="autopilot-highlight">
    <div style={{ width: 20, height: 20, borderRadius: 5, background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}><SparkleIcon size={12} color="#fff" /></div>
    <div>
      <p style={{ fontSize: 11, fontWeight: 500, color: '#111' }}>Autopilot handles clean claims automatically</p>
      <p style={{ fontSize: 10, color: '#555', lineHeight: '1.4', marginTop: 2 }}>Claims scoring &ge;90% and passing all checks are submitted without your review. You only see what truly needs attention &mdash; exceptions, flags, and denials.</p>
    </div>
  </div>
);

const BADGE = {
  billing: { background: '#f0f0f0', color: '#555' },
  ai: { background: '#f0f0f0', color: '#555' },
  aiDraft: { background: '#f0f0f0', color: '#555' },
  lens: { background: '#f9f9f9', color: '#555', border: '0.5px solid #e0e0e0' },
  fhir: { background: '#eff6ff', color: '#1e40af' },
  autopilot: { background: '#111', color: '#fff' },
};

/* ── The four workflow combinations ── */
const CombinationA = () => (
  <div className="flex flex-col gap-2">
    <AIStep title="Claims sync and scrub overnight" badge="Automatic" badgeStyle={BADGE.ai} desc="RevQ pulls encounters from your EHR via FHIR and validates every claim — coding, modifiers, payer rules, denial risk. Clean claims are ready by morning." />
    <Divider label="You pick up here each morning" />
    <HumanStep num={1} title="Review flagged claims and submit" badge="Billing Focus" badgeStyle={BADGE.billing} desc="Open Billing Focus to see your queue. Clean claims submit in one click. Flagged claims show exactly what to fix. 'Submit all clean' batches everything that's ready." />
    <Divider label="RevQ monitors payments and denials" />
    <AIStep title="AI posts payments and flags denials" badge="AI" badgeStyle={BADGE.ai} desc="Payments post via FHIR. Denied claims surface with the reason in plain English and a pre-written appeal draft." />
    <HumanStep num={2} title="Work denials — AI writes the appeal" badge="AI draft" badgeStyle={BADGE.aiDraft} desc="Review the pre-written appeal, edit if needed, and send. RevQ tracks resubmission deadlines automatically." label="You + AI" />
  </div>
);

const CombinationB = ({ onLensClick }) => (
  <div className="flex flex-col gap-2">
    <HumanStep num={1} title="Scan your superbills and insurance cards" badge="Lens" badgeStyle={BADGE.lens} desc="Photograph or upload the day's superbills and any insurance cards. RevQ extracts patient data, procedure codes, diagnoses, and payer info automatically — no manual entry." lensCta="Try scanning — see it in action" onLensClick={onLensClick} />
    <Divider label="RevQ AI takes over" />
    <AIStep title="AI scrubs, validates, and prepares claims" badge="AI" badgeStyle={BADGE.ai} desc="RevQ checks every claim for coding errors, missing modifiers, payer-specific rules, and denial risks. Clean claims go to 'Ready.' Flagged claims are queued for review with a fix attached." />
    <HumanStep num={2} title="Review flagged claims, then submit" badge="Billing Focus" badgeStyle={BADGE.billing} desc="Open Billing Focus. Clean claims submit in one click. Flagged claims show what to fix. 'Submit all clean' batches everything that's ready." />
    <Divider label="RevQ monitors payments and denials" />
    <AIStep title="AI posts payments and flags denials" badge="AI + Lens" badgeStyle={BADGE.ai} desc="When EOBs arrive, scan them with Lens. RevQ auto-posts payments, reconciles balances, and surfaces denied claims with a plain-English reason and appeal draft." label="AI + Lens" />
    <HumanStep num={3} title="Work denials — AI writes the appeal" badge="AI draft" badgeStyle={BADGE.aiDraft} desc="Review the pre-written appeal, edit if needed, send. RevQ tracks resubmission deadlines automatically." label="You + AI" />
  </div>
);

const CombinationC = () => (
  <div className="flex flex-col gap-2">
    <AutopilotHighlight />
    <AutopilotStep title="Claims sync, scrub, and auto-submit" badge="Autopilot" desc="RevQ pulls encounters from your EHR via FHIR, validates every claim, and auto-submits all clean claims. You're notified what went out. Nothing requires your attention unless there's an exception." />
    <Divider label="You step in only for exceptions" />
    <HumanStep num={1} title="Review flagged claims" badge="Billing Focus" badgeStyle={BADGE.billing} desc="Only claims Autopilot couldn't auto-submit land in your queue — the ones with issues that need a judgment call. Each shows exactly what to fix. Most days this queue is short." />
    <Divider label="Autopilot monitors the rest" />
    <AutopilotStep title="AI posts payments, flags denials, tracks deadlines" badge="Autopilot" desc="Payment posting, denial detection, CARC code translation, and appeal drafting all happen automatically. Denied claims surface in your queue when ready with a pre-written appeal attached." label="AI handles this" />
  </div>
);

const CombinationD = ({ onLensClick }) => (
  <div className="flex flex-col gap-2">
    <AutopilotHighlight />
    <HumanStep num={1} title="Scan your superbills and insurance cards" badge="Lens" badgeStyle={BADGE.lens} desc="Photograph or upload the day's documents. RevQ extracts all data and begins processing immediately." lensCta="Try scanning — see it in action" onLensClick={onLensClick} />
    <Divider label="Autopilot takes over" />
    <AutopilotStep title="Autopilot scrubs, validates, and auto-submits clean claims" badge="Autopilot" desc="Every claim is validated against payer rules, coding standards, and denial risk. Claims scoring &ge;90% are submitted automatically. You're notified of what went out." />
    <HumanStep num={2} title="Review exceptions in Billing Focus" badge="Billing Focus" badgeStyle={BADGE.billing} desc="Only flagged claims land in your queue — the ones Autopilot couldn't auto-submit. Each shows exactly what to fix. Your daily review time drops significantly." />
    <Divider label="Autopilot monitors the rest" />
    <AutopilotStep title="AI posts payments and flags denials" badge="AI + Lens" desc="Scan EOBs with Lens. Payments post automatically, denied claims surface with a plain-English reason and pre-written appeal draft." lensCta="Try scanning an EOB" onLensClick={onLensClick} label="AI + Lens" />
  </div>
);

const STEP_COUNTS = { 'assist-fhir': 2, 'assist-non-fhir': 3, 'autopilot-fhir': 1, 'autopilot-non-fhir': 2 };

/* ── Main Lightbox ── */
const RevQOnboardingLightbox = ({ open, onClose, hasFhir = false }) => {
  const navigate = useNavigate();

  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem(MODE_KEY) || 'assist'; } catch { return 'assist'; }
  });
  const [integration, setIntegration] = useState(() => {
    try { return localStorage.getItem(INTEGRATION_KEY) || (hasFhir ? 'fhir' : 'non-fhir'); } catch { return hasFhir ? 'fhir' : 'non-fhir'; }
  });

  useEffect(() => {
    try { localStorage.setItem(MODE_KEY, mode); } catch {}
  }, [mode]);
  useEffect(() => {
    try { localStorage.setItem(INTEGRATION_KEY, integration); } catch {}
  }, [integration]);

  if (!open) return null;

  const comboKey = `${mode}-${integration}`;
  const stepCount = STEP_COUNTS[comboKey];
  const isAutopilot = mode === 'autopilot';

  const handleCta = () => {
    onClose();
    navigate('/revq/billing-focus');
  };

  const handleLensClick = () => {
    /* placeholder — opens Lens demo lightbox if wired */
  };

  const handleDontShow = (e) => {
    try {
      if (e.target.checked) localStorage.setItem(LIGHTBOX_DISMISSED_KEY, 'true');
      else localStorage.removeItem(LIGHTBOX_DISMISSED_KEY);
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="workflow-lightbox">
      <div className="bg-white shadow-2xl w-full mx-4 flex flex-col" style={{ borderRadius: 14, maxWidth: 660, maxHeight: '90vh', animation: 'onbFadeIn 0.2s ease' }}>
        {/* ── Header ── */}
        <div style={{ padding: '18px 20px 0', flexShrink: 0 }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src={REVQ_LOGO_BLACK} alt="RevQ" style={{ height: 14, width: 'auto', objectFit: 'contain' }} />
              <span style={{ fontSize: 10, color: '#111', letterSpacing: '.06em', fontWeight: 500 }}>&middot; Getting started</span>
            </div>
            <button onClick={onClose} className="flex items-center justify-center" style={{ width: 26, height: 26, borderRadius: '50%', background: '#f5f5f5', border: 'none', cursor: 'pointer' }} data-testid="close-workflow-lightbox">
              <X style={{ width: 13, height: 13, color: '#888' }} />
            </button>
          </div>
          <h2 style={{ fontSize: 17, fontWeight: 500, color: '#111', marginTop: 8 }}>Your billing, on autopilot</h2>
          <p style={{ fontSize: 11, color: '#888', marginTop: 3 }}>Choose how involved you want to be &mdash; you can change this any time</p>

          {/* ── Layer 1: Mode selector ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 14 }}>
            {/* Assist card */}
            <button
              onClick={() => setMode('assist')}
              style={{ textAlign: 'left', background: '#fff', border: `1.5px solid ${mode === 'assist' ? '#111' : '#e0e0e0'}`, borderRadius: 10, padding: '12px 14px', cursor: 'pointer', position: 'relative', transition: 'border-color 0.15s' }}
              data-testid="mode-card-assist"
            >
              <div style={{ position: 'absolute', top: 12, right: 14, width: 16, height: 16, borderRadius: '50%', border: `1.5px solid ${mode === 'assist' ? '#111' : '#e0e0e0'}`, background: mode === 'assist' ? '#111' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {mode === 'assist' && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap" style={{ paddingRight: 24 }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: '#111' }}>Assist mode</span>
                <span style={{ fontSize: 9, fontWeight: 500, padding: '1px 6px', borderRadius: 8, background: '#f0f0f0', color: '#555' }}>Default</span>
              </div>
              <p style={{ fontSize: 10, color: '#888', lineHeight: '1.4', marginTop: 5 }}>You review every claim before it submits. RevQ validates, flags issues, and suggests fixes &mdash; you stay in control of every submission.</p>
            </button>
            {/* Autopilot card */}
            <button
              onClick={() => setMode('autopilot')}
              style={{ textAlign: 'left', background: '#fff', border: `1.5px solid ${mode === 'autopilot' ? '#111' : '#e0e0e0'}`, borderRadius: 10, padding: '12px 14px', cursor: 'pointer', position: 'relative', transition: 'border-color 0.15s' }}
              data-testid="mode-card-autopilot"
            >
              <div style={{ position: 'absolute', top: 12, right: 14, width: 16, height: 16, borderRadius: '50%', border: `1.5px solid ${mode === 'autopilot' ? '#111' : '#e0e0e0'}`, background: mode === 'autopilot' ? '#111' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {mode === 'autopilot' && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap" style={{ paddingRight: 24 }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: '#111' }}>Autopilot</span>
                <span style={{ fontSize: 9, fontWeight: 500, padding: '1px 6px', borderRadius: 8, background: '#111', color: '#fff' }}>AI-first</span>
              </div>
              <p style={{ fontSize: 10, color: '#888', lineHeight: '1.4', marginTop: 5 }}>RevQ auto-submits clean claims and only surfaces exceptions. You review flagged items and denials &mdash; the routine is handled for you.</p>
            </button>
          </div>

          {/* ── Layer 2: Integration type ── */}
          <div style={{ margin: '10px 0 0', padding: 3, background: '#f5f5f5', borderRadius: 7, display: 'flex', gap: 2 }}>
            <button
              onClick={() => setIntegration('fhir')}
              style={{ flex: 1, fontSize: 10, fontWeight: integration === 'fhir' ? 500 : 400, color: integration === 'fhir' ? '#111' : '#888', background: integration === 'fhir' ? '#fff' : 'transparent', border: integration === 'fhir' ? '0.5px solid #e8e8e8' : 'none', borderRadius: 5, padding: '5px 0', cursor: 'pointer', transition: 'all 0.15s' }}
              data-testid="integration-tab-fhir"
            >FHIR connected</button>
            <button
              onClick={() => setIntegration('non-fhir')}
              style={{ flex: 1, fontSize: 10, fontWeight: integration === 'non-fhir' ? 500 : 400, color: integration === 'non-fhir' ? '#111' : '#888', background: integration === 'non-fhir' ? '#fff' : 'transparent', border: integration === 'non-fhir' ? '0.5px solid #e8e8e8' : 'none', borderRadius: 5, padding: '5px 0', cursor: 'pointer', transition: 'all 0.15s' }}
              data-testid="integration-tab-non-fhir"
            >Non-FHIR &middot; using Lens</button>
          </div>

          {/* Conditional info bar below toggle */}
          {integration === 'fhir' ? (
            <div style={{ background: '#eff6ff', border: '0.5px solid #bfdbfe', borderRadius: 7, padding: '7px 11px', marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }} data-testid="fhir-sync-note">
              <div style={{ width: 16, height: 16, borderRadius: 3, background: '#bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: 7, fontWeight: 700, color: '#1e40af' }}>F</span>
              </div>
              <p style={{ fontSize: 10, color: '#1e40af', lineHeight: '1.4' }}>Your EHR syncs claims via FHIR &mdash; RevQ begins processing when encounters close. No scanning needed.</p>
            </div>
          ) : (
            <div style={{ background: '#f9f9f9', border: '0.5px solid #e8e8e8', borderRadius: 8, padding: '10px 13px', marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }} data-testid="lens-hero-bar">
              <div style={{ width: 32, height: 32, borderRadius: 7, background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Search style={{ width: 16, height: 16, color: '#fff' }} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 11, fontWeight: 500, color: '#111' }}>Lens is your bridge to RevQ</p>
                <p style={{ fontSize: 10, color: '#555', marginTop: 1 }}>Scan superbills, insurance cards, and EOBs &mdash; RevQ extracts everything automatically.</p>
              </div>
              <button onClick={handleLensClick} style={{ height: 28, padding: '0 12px', borderRadius: 6, background: '#111', color: '#fff', fontSize: 10, fontWeight: 500, border: 'none', cursor: 'pointer', flexShrink: 0 }} data-testid="try-lens-demo-btn">Try Lens demo</button>
            </div>
          )}
        </div>

        {/* ── Body: Workflow steps ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px 16px' }}>
          {/* Step count */}
          <p style={{ fontSize: 9, color: '#bbb', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
            Your daily workflow &mdash; {stepCount} step{stepCount !== 1 ? 's' : ''}
          </p>

          {/* Steps based on combination */}
          {comboKey === 'assist-fhir' && <CombinationA />}
          {comboKey === 'assist-non-fhir' && <CombinationB onLensClick={handleLensClick} />}
          {comboKey === 'autopilot-fhir' && <CombinationC />}
          {comboKey === 'autopilot-non-fhir' && <CombinationD onLensClick={handleLensClick} />}
        </div>

        {/* ── Footer ── */}
        <div style={{ borderTop: '0.5px solid #f0f0f0', padding: '10px 20px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
            <input type="checkbox" style={{ accentColor: '#555' }} onChange={handleDontShow} data-testid="dont-show-again-checkbox" />
            <span style={{ fontSize: 10, color: '#aaa' }}>Don't show this again</span>
          </label>
          <button
            onClick={handleCta}
            className="flex items-center gap-1.5"
            style={{ height: 32, padding: '0 18px', borderRadius: 7, background: '#111', color: '#fff', fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer' }}
            data-testid="onboarding-cta-btn"
          >
            <SparkleIcon size={11} color="#fff" />
            {isAutopilot ? 'Start Autopilot' : 'Enter Billing Focus'}
            <ArrowRight style={{ width: 12, height: 12, opacity: 0.6 }} />
          </button>
        </div>
      </div>
      <style>{`@keyframes onbFadeIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }`}</style>
    </div>
  );
};

export default RevQOnboardingLightbox;
export { LIGHTBOX_DISMISSED_KEY, MODE_KEY, INTEGRATION_KEY };
